<script>
export default {
    name: 'ServicePlanFeatures',
};
</script>

<script setup>
import { computed } from 'vue';
import { FIELD } from '@/components/payments/components/service-plan/components/features/constants.js';
import { getFeatureComponent } from '@/components/payments/components/service-plan/components/features/utils.js';
import useFeatures from '@/components/payments/components/service-plan/components/features/composables/use-features.js';

const props = defineProps({
    features: {
        type: Object,
        required: true,
    },
    itemClass: {
        type: [String, Object, Array],
    },
    inline: {
        type: Boolean,
    },
    asList: {
        type: Boolean,
    },
    featuresParams: {
        type: Object,
        default: () => ({}),
    },
});

const { visibleFeatures, exhaustion } = useFeatures(
    () => props.features,
    () => props.featuresParams,
);

const list = computed(() => Object.entries(visibleFeatures.value).map(([name, feature]) => {
    const featureName = feature[FIELD.NAME] ?? name;

    return {
        name: featureName,
        feature,
        exhaustion: exhaustion.value[featureName],
        component: getFeatureComponent(featureName),
    };
}));

function hasVisibleExhaustion(exhaustion) {
    return exhaustion?.exhausted && exhaustion?.message;
}
</script>

<template>
    <div
        :class="{
            'grid list-outside list-disc pl-4 *:list-item': asList,
        }"
    >
        <slot name="prefix" />

        <base-tooltip
            v-for="({ name, feature, exhaustion, component }) in list"
            :key="name"
            class="!leading-[inherit]"
            :class="{
                '!ml-0 list-none': asList && hasVisibleExhaustion(exhaustion),
            }"
            :disabled="!hasVisibleExhaustion(exhaustion)"
            :content="exhaustion?.message"
        >
            <div class="relative">
                <slot
                    name="item-prefix"
                    :feature="feature"
                    :exhaustion="exhaustion"
                />

                <div
                    v-if="hasVisibleExhaustion(exhaustion)"
                    :class="{
                        'absolute -ml-5': asList,
                    }"
                >
                    <base-icon
                        class="mr-2 inline-block text-sm text-amber-500"
                        name="exclamation-triangle"
                    />
                </div>

                <component
                    :is="component"
                    :feature="feature"
                    :inline="inline"
                />
            </div>
        </base-tooltip>

        <slot name="suffix" />
    </div>
</template>
